import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import useAppointments from 'state/appointments';
import useUser from 'state/user';
import { AppointmentStatusValues, ClientTherapistStatus } from 'resources/constants/config';
import { IMAGES_URL } from 'resources/constants/urls';
import {
    BRAND_GREEN_PRIMARY,
    DARKER_TEXT,
    DARK_TEXT,
    PLACEHOLDER_TEXT,
    SUCCESS_GREEN,
    WARNING_ORANGE,
    RED,
    ERROR_RED,
} from 'resources/constants/colors';
import NoProfileSVG from 'resources/img/no-pic-therapist.png';
import { getDisplayDate } from 'utils/date';
import { getDisplayTime } from 'utils/time';
import { getStatusColor } from 'utils';
import { getStatusText } from 'utils/text';

const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
`;

// ---------------------------------------------------------------------------
// Main card
// ---------------------------------------------------------------------------

const Card = styled.div`
    background: #fff;
    border-radius: 20px;
    padding: 16px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.07);
    animation: ${fadeUp} 0.4s 0.1s ease both;
`;

const CardHeader = styled.div`
    display: flex;
    gap: 14px;
    align-items: center;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &:active { opacity: 0.85; }
`;

const Avatar = styled.div`
    width: 56px;
    height: 56px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
    background: #eee;
    border: 2px solid ${BRAND_GREEN_PRIMARY}40;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const Info = styled.div`
    flex: 1;
    min-width: 0;
`;

const Name = styled.p`
    font-size: 15px;
    font-weight: 700;
    color: ${DARKER_TEXT};
    margin: 0 0 2px;
`;

const Role = styled.p`
    font-size: 12px;
    color: ${PLACEHOLDER_TEXT};
    margin: 0 0 4px;
`;

const StatusBadge = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: ${({ pending }) => pending ? WARNING_ORANGE : SUCCESS_GREEN};
    background: ${({ pending }) => pending ? `${WARNING_ORANGE}18` : `${SUCCESS_GREEN}18`};
    border-radius: 20px;
    padding: 2px 8px;
    display: inline-block;
`;

const Arrow = styled.span`
    font-size: 18px;
    color: ${PLACEHOLDER_TEXT};
    flex-shrink: 0;
    transition: transform 0.2s;
    transform: ${({ open }) => open ? 'rotate(90deg)' : 'rotate(0deg)'};
`;

// ---------------------------------------------------------------------------
// Expandable detail
// ---------------------------------------------------------------------------

const DetailPanel = styled.div`
    overflow: hidden;
    max-height: ${({ open }) => open ? '800px' : '0'};
    opacity: ${({ open }) => open ? 1 : 0};
    transition: max-height 0.35s ease, opacity 0.25s ease;
`;

const DetailInner = styled.div`
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
    margin-top: 14px;
`;

const ActionRow = styled.div`
    display: flex;
    gap: 10px;
    margin-bottom: 18px;
`;

const ActionButton = styled(Link)`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    background: ${BRAND_GREEN_PRIMARY};
    color: #fff;
    border-radius: 12px;
    padding: 10px 14px;
    font-size: 13px;
    font-weight: 700;
    text-decoration: none;
    -webkit-tap-highlight-color: transparent;

    &:active { opacity: 0.85; }
`;

const SecondaryButton = styled(Link)`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    background: transparent;
    color: ${BRAND_GREEN_PRIMARY};
    border: 1.5px solid ${BRAND_GREEN_PRIMARY}60;
    border-radius: 12px;
    padding: 10px 14px;
    font-size: 13px;
    font-weight: 700;
    text-decoration: none;
    -webkit-tap-highlight-color: transparent;

    &:active { opacity: 0.85; }
`;

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------

const HistoryTitle = styled.p`
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    color: #aaa;
    margin: 0 0 10px;
`;

const HistoryList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 200px;
    overflow-y: auto;
`;

const HistoryItem = styled(Link)`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    background: #fafafa;
    border-radius: 12px;
    text-decoration: none;
    -webkit-tap-highlight-color: transparent;

    &:active { opacity: 0.85; }
`;

const HistoryDot = styled.div`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ color }) => color || '#ccc'};
    flex-shrink: 0;
`;

const HistoryInfo = styled.div`
    flex: 1;
    min-width: 0;
`;

const HistoryDate = styled.p`
    font-size: 13px;
    font-weight: 600;
    color: ${DARKER_TEXT};
    margin: 0;
`;

const HistoryStatus = styled.p`
    font-size: 11px;
    color: ${({ color }) => color || PLACEHOLDER_TEXT};
    margin: 0;
`;

const EmptyHistory = styled.p`
    font-size: 13px;
    color: ${PLACEHOLDER_TEXT};
    text-align: center;
    padding: 16px 0;
    margin: 0;
`;

// ---------------------------------------------------------------------------
// Pending assignment actions
// ---------------------------------------------------------------------------

const AssignmentBanner = styled.div`
    background: ${WARNING_ORANGE}12;
    border: 1.5px solid ${WARNING_ORANGE}40;
    border-radius: 14px;
    padding: 14px 16px;
    margin-top: 14px;
`;

const AssignmentBannerText = styled.p`
    font-size: 13px;
    color: ${DARKER_TEXT};
    text-align: center;
    margin: 0 0 12px;
    line-height: 1.5;
`;

const AssignmentActions = styled.div`
    display: flex;
    gap: 10px;
`;

const AcceptButton = styled.button`
    flex: 1;
    background: ${BRAND_GREEN_PRIMARY};
    border: none;
    border-radius: 12px;
    padding: 11px;
    font-size: 14px;
    font-weight: 700;
    color: #fff;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &:disabled { opacity: 0.5; cursor: default; }
    &:active { opacity: 0.85; }
`;

const RejectButton = styled.button`
    flex: 1;
    background: #f5f5f5;
    border: none;
    border-radius: 12px;
    padding: 11px;
    font-size: 14px;
    font-weight: 600;
    color: ${ERROR_RED};
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &:disabled { opacity: 0.5; cursor: default; }
    &:active { opacity: 0.85; }
`;

// ---------------------------------------------------------------------------
// Change therapist
// ---------------------------------------------------------------------------

const DangerButton = styled.button`
    width: 100%;
    background: none;
    border: 1.5px solid ${RED}40;
    border-radius: 12px;
    padding: 10px 14px;
    font-size: 13px;
    font-weight: 600;
    color: ${RED};
    cursor: pointer;
    margin-top: 18px;
    -webkit-tap-highlight-color: transparent;

    &:active { opacity: 0.7; }
    &:disabled { opacity: 0.5; cursor: default; }
`;

// ---------------------------------------------------------------------------
// Confirmation dialog
// ---------------------------------------------------------------------------

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.5);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
`;

const Dialog = styled.div`
    background: #fff;
    border-radius: 20px;
    padding: 24px 20px;
    max-width: 340px;
    width: 100%;
    text-align: center;
`;

const DialogTitle = styled.p`
    font-size: 16px;
    font-weight: 700;
    color: ${DARKER_TEXT};
    margin: 0 0 10px;
`;

const DialogBody = styled.p`
    font-size: 13px;
    color: ${DARK_TEXT};
    line-height: 1.55;
    margin: 0 0 20px;
`;

const DialogActions = styled.div`
    display: flex;
    gap: 10px;
`;

const DialogCancel = styled.button`
    flex: 1;
    background: #f5f5f5;
    border: none;
    border-radius: 12px;
    padding: 12px;
    font-size: 14px;
    font-weight: 600;
    color: ${DARK_TEXT};
    cursor: pointer;
`;

const DialogConfirm = styled.button`
    flex: 1;
    background: ${RED};
    border: none;
    border-radius: 12px;
    padding: 12px;
    font-size: 14px;
    font-weight: 700;
    color: #fff;
    cursor: pointer;

    &:disabled { opacity: 0.5; cursor: default; }
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TherapistDetail = ({ therapist, isPending }) => {
    const [open, setOpen] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [appointments, appointmentsDispatcher] = useAppointments();
    const [user, userDispatcher] = useUser();
    const isRemoving = user.fetching.removeAssignment?.state;
    const isAccepting = user.fetching.acceptInvitation?.state;

    const pendingInvitation = useMemo(() => {
        const invitations = user.current?.extraData?.invitations ?? [];
        return invitations.find(
            (inv) => inv.status === 'pending' || inv.status === ClientTherapistStatus.PENDING
        ) ?? null;
    }, [user.current?.extraData?.invitations]);

    const hasAlreadyAccepted = pendingInvitation?.accepted === true;

    const handleAcceptAssignment = (accept) => {
        if (!pendingInvitation) return;
        userDispatcher.acceptInvitationStart({
            accept,
            invitationUUID: pendingInvitation.invitationUUID,
        });
    };

    useEffect(() => {
        if (open && appointments.list.length === 0) {
            appointmentsDispatcher.fetchStart();
        }
    }, [open]);

    const therapistHistory = useMemo(() => {
        if (!therapist?.id) return [];
        return appointments.list
            .filter((a) => String(a.therapistId) === String(therapist.id))
            .sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [appointments.list, therapist?.id]);

    const handleRemove = () => {
        userDispatcher.removeAssignmentStart({ therapistId: therapist.id });
        setShowConfirm(false);
    };

    return (
        <>
            <Card>
                <CardHeader onClick={() => setOpen(!open)}>
                    <Avatar>
                        <img
                            src={
                                therapist.profileImg
                                    ? `${IMAGES_URL}${therapist.profileImg}`
                                    : NoProfileSVG
                            }
                            alt={therapist.name}
                        />
                    </Avatar>
                    <Info>
                        <Name>
                            {`${therapist.title ?? ''} ${therapist.name ?? ''} ${therapist.lastName ?? ''}`.trim()}
                        </Name>
                        <Role>Psicólogo / Terapeuta</Role>
                        <StatusBadge pending={isPending}>
                            {isPending ? '⏳ Asignación pendiente' : '✓ Activo'}
                        </StatusBadge>
                    </Info>
                    <Arrow open={open}>›</Arrow>
                </CardHeader>

                {isPending && pendingInvitation && (
                    <AssignmentBanner>
                        {hasAlreadyAccepted ? (
                            <AssignmentBannerText>
                                ✓ Ya aceptaste la invitación. Esperando confirmación del terapeuta.
                            </AssignmentBannerText>
                        ) : (
                            <>
                                <AssignmentBannerText>
                                    Tu terapeuta te ha enviado una invitación de asignación.
                                    Ambos deben aceptar para confirmar la relación terapéutica.
                                </AssignmentBannerText>
                                <AssignmentActions>
                                    <RejectButton
                                        onClick={() => handleAcceptAssignment(false)}
                                        disabled={isAccepting}
                                    >
                                        Rechazar
                                    </RejectButton>
                                    <AcceptButton
                                        onClick={() => handleAcceptAssignment(true)}
                                        disabled={isAccepting}
                                    >
                                        {isAccepting ? 'Procesando...' : '✓ Aceptar'}
                                    </AcceptButton>
                                </AssignmentActions>
                            </>
                        )}
                    </AssignmentBanner>
                )}

                <DetailPanel open={open}>
                    <DetailInner>
                        <ActionRow>
                            <ActionButton to={{ pathname: `/terapeutas/${therapist.id}`, state: { from: '/home/resumen' } }}>
                                📅 Agendar cita
                            </ActionButton>
                            <SecondaryButton to="/home/mi-terapeuta">
                                👤 Ver perfil
                            </SecondaryButton>
                        </ActionRow>

                        <HistoryTitle>Historial de citas</HistoryTitle>
                        {therapistHistory.length > 0 ? (
                            <HistoryList>
                                {therapistHistory.map((appt) => {
                                    const color = getStatusColor(appt);
                                    const statusLabel = getStatusText(appt);
                                    const isCompleted = appt.status === AppointmentStatusValues.COMPLETED;
                                    return (
                                        <HistoryItem
                                            key={appt.id || appt.roomId}
                                            to={`/appointment/${appt.roomId}`}
                                        >
                                            <HistoryDot color={color} />
                                            <HistoryInfo>
                                                <HistoryDate>
                                                    {getDisplayDate(appt.date, 'd MMM yyyy')} · {getDisplayTime(appt.date)}
                                                </HistoryDate>
                                                <HistoryStatus color={color}>
                                                    {statusLabel} {isCompleted && '✓'}
                                                </HistoryStatus>
                                            </HistoryInfo>
                                        </HistoryItem>
                                    );
                                })}
                            </HistoryList>
                        ) : (
                            <EmptyHistory>
                                No hay citas registradas aún
                            </EmptyHistory>
                        )}

                        {!isPending && (
                            <DangerButton
                                onClick={() => setShowConfirm(true)}
                                disabled={isRemoving}
                            >
                                Cambiar de terapeuta
                            </DangerButton>
                        )}
                    </DetailInner>
                </DetailPanel>
            </Card>

            {showConfirm && (
                <Overlay onClick={() => setShowConfirm(false)}>
                    <Dialog onClick={(e) => e.stopPropagation()}>
                        <DialogTitle>⚠️ Cambiar de terapeuta</DialogTitle>
                        <DialogBody>
                            Al confirmar, se cancelarán las citas pendientes y se
                            cerrará la conversación con tu terapeuta actual.
                            <br /><br />
                            <strong>Esta acción no se puede deshacer.</strong>
                            {' '}Una vez terminada la relación terapéutica, no
                            podrás restablecerla con este terapeuta.
                        </DialogBody>
                        <DialogActions>
                            <DialogCancel onClick={() => setShowConfirm(false)}>
                                Cancelar
                            </DialogCancel>
                            <DialogConfirm
                                onClick={handleRemove}
                                disabled={isRemoving}
                            >
                                {isRemoving ? 'Procesando...' : 'Confirmar'}
                            </DialogConfirm>
                        </DialogActions>
                    </Dialog>
                </Overlay>
            )}
        </>
    );
};

export default TherapistDetail;
